import PropTypes from 'prop-types';
import React from 'react';
import { AboveTheFoldOnlyServerRender } from 'above-the-fold-only-server-render';
import { FormattedMessage } from 'react-intl';

import { ChevronLeft } from '@bibliocommons/base-icons';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';
import { isConjunctiveField } from '@bibliocommons/utils-filter';
import { withRouter } from '@bibliocommons/utils-routing';
import { bindAll } from '@bibliocommons/utils-react';
import * as Drawer from 'app/components/shared/DrawerLayout';
import { TextButton } from '@bibliocommons/deprecated-base-buttons';
import { routerShape } from '@bibliocommons/bc-prop-types';

import getToggledFilter from 'app/helpers/facets/getToggledFilter';
import formatFiltersForQuery from 'app/helpers/facets/formatFiltersForQuery';
import { activeFiltersShape, fieldsShape } from 'app/shapes/search';
import CheckboxField from '../CheckboxField';
import PrivacyField from '../PrivacyField';
import FormatField from '../FormatField';
import LanguageField from '../LanguageField';
import SimpleField from '../SimpleField';
import TagGroupField from '../TagGroupField';
import StatusField from '../StatusField';

import './FieldList.scss';

export class FieldList extends React.PureComponent {
  constructor(props) {
    super(props);
    bindAll(this);
  }

  handleToggleFilter(fieldId, currentFilter, override = false) {
    const { activeFilters, paginationField, router, separator, prefix, transformQuery } = this.props;

    let query = {
      ...router.query,
      [paginationField]: undefined,
      ...formatFiltersForQuery(activeFilters, separator, prefix),
      ...getToggledFilter({ fieldId, currentFilter, activeFilters, separator, prefix, override })
    };

    if (transformQuery) {
      query = transformQuery(query);
    }

    router.push({ query });
  }

  handleToggleExpandedField(fieldId, currentFilter) {
    this.handleToggleFilter(fieldId, currentFilter);
  }

  isFieldExpanded(field) {
    if (!field) return false;
    return field.get('expanded') || this.props.activeFilters.has(field.get('id'));
  }

  renderFields(fieldId) {
    const {
      handleExpandField,
      renderSearchScope,
      fields,
      activeFilters,
      handleCloseExpandField,
      router,
      transformQuery,
      singleBranch
    } = this.props;
    const handleToggleFilter = this.props.handleToggleFilter || this.handleToggleFilter;
    const handleToggleExpandedField = this.props.handleToggleExpandedField || this.handleToggleExpandedField;

    let fieldTag;
    switch (fieldId) {
      case 'SEARCH_SCOPE': {
        fieldTag = renderSearchScope ? renderSearchScope() : null;
        break;
      }
      case 'IS_PRIVATE': {
        const field = fields.find(f => f.get('id') === fieldId);

        if (!field) {
          return null;
        }

        fieldTag = <PrivacyField handleToggleFilter={handleToggleFilter} filters={field.get('fieldFilters')} />;
        break;
      }
      case 'I_OWN_THIS': {
        const field = fields.find(f => f.get('id') === fieldId);
        const fieldFilter = field && field.get('fieldFilters').find(filter => `${filter.get('value')}` === 'true');

        if (!fieldFilter) {
          return null;
        }

        fieldTag = (
          <CheckboxField key={fieldId} fieldId={fieldId} filter={fieldFilter} handleToggleFilter={handleToggleFilter} />
        );
        break;
      }
      case 'TAGS': {
        const genreTagField = fields.find(f => f.get('id') === 'TAG_GENRE');
        const toneTagField = fields.find(f => f.get('id') === 'TAG_TONE');
        const aboutTagField = fields.find(f => f.get('id') === 'TAG_ABOUT');
        const personalTagField = fields.find(f => f.get('id') === 'TAG_PERSONAL');

        if (!(genreTagField || toneTagField || aboutTagField || personalTagField)) {
          return null;
        }

        fieldTag = (
          <AboveTheFoldOnlyServerRender skip>
            <TagGroupField
              key="TAG_GROUP"
              genreTagField={genreTagField}
              genreExpanded={this.isFieldExpanded(genreTagField)}
              toneTagField={toneTagField}
              toneExpanded={this.isFieldExpanded(toneTagField)}
              aboutTagField={aboutTagField}
              aboutExpanded={this.isFieldExpanded(aboutTagField)}
              personalTagField={personalTagField}
              personalExpanded={this.isFieldExpanded(personalTagField)}
              handleToggleFilter={handleToggleFilter}
              handleExpandField={handleExpandField}
              activeFilters={activeFilters}
              handleCloseExpandField={handleCloseExpandField}
              handleToggleExpandedField={handleToggleExpandedField}
            />
          </AboveTheFoldOnlyServerRender>
        );
        break;
      }
      case 'LANGUAGES': {
        let field = null;
        const primaryLanguage = fields.find(f => f.get('id') === 'PRIMARY_LANGUAGE');
        const language = fields.find(f => f.get('id') === 'LANGUAGE');

        if (!(primaryLanguage || language)) {
          return null;
        }

        if (!primaryLanguage && language) {
          field = language;
        }

        if (primaryLanguage && !language) {
          field = primaryLanguage;
        }

        if (field) {
          fieldTag = (
            <AboveTheFoldOnlyServerRender skip>
              <SimpleField
                key={field.get('id')}
                label="LANGUAGE.field"
                fieldId={field.get('id')}
                hasMore={field.get('hasMore')}
                filters={field.get('fieldFilters')}
                handleExpandField={handleExpandField}
                handleToggleFilter={handleToggleFilter}
                activeFilters={activeFilters}
                initialIsExpanded={this.isFieldExpanded(field)}
                handleCloseExpandField={handleCloseExpandField}
                handleToggleExpandedField={handleToggleExpandedField}
              />
            </AboveTheFoldOnlyServerRender>
          );
        } else {
          fieldTag = (
            <AboveTheFoldOnlyServerRender skip>
              <LanguageField
                key="LANGUAGES"
                primaryLanguage={primaryLanguage}
                language={language}
                handleToggleFilter={handleToggleFilter}
                handleExpandField={handleExpandField}
                handleCloseExpandField={handleCloseExpandField}
                activeFilters={activeFilters}
                handleToggleExpandedField={handleToggleExpandedField}
                transformQuery={transformQuery}
                router={router}
              />
            </AboveTheFoldOnlyServerRender>
          );
        }
        break;
      }
      case 'FORMAT': {
        const formatField = fields.find(f => f.get('id') === fieldId);

        if (!formatField) {
          return null;
        }

        fieldTag = (
          <FormatField
            key="FORMAT"
            fieldId={formatField.get('id')}
            filters={formatField.get('fieldFilters')}
            handleToggleFilter={handleToggleFilter}
            activeFilters={activeFilters}
          />
        );
        break;
      }
      case 'STATUS': {
        const statusField = fields.find(f => f.get('id') === fieldId);

        if (!statusField) {
          return null;
        }

        fieldTag = (
          <StatusField
            key="STATUS"
            fieldId={statusField.get('id')}
            filters={statusField.get('fieldFilters')}
            handleToggleFilter={handleToggleFilter}
            activeFilters={activeFilters}
            singleBranch={singleBranch}
            isConjunctive={isConjunctiveField(fieldId)}
            hasMore={statusField.get('hasMore')}
            handleExpandField={handleExpandField}
            initialIsExpanded={this.isFieldExpanded(statusField)}
            handleCloseExpandField={handleCloseExpandField}
            handleToggleExpandedField={handleToggleExpandedField}
          />
        );
        break;
      }
      // STATUS must be the last case before default so it can fallthrough on non single branch libraries
      default: {
        // eslint-disable-line no-fallthrough
        const field = fields.find(f => f.get('id') === fieldId);

        if (!field || field.get('fieldFilters').size === 0) {
          return null;
        }

        fieldTag = (
          <AboveTheFoldOnlyServerRender skip>
            <SimpleField
              isConjunctive={isConjunctiveField(fieldId)}
              key={field.get('id')}
              label={field.get('labelId') || `${field.get('id')}.field`}
              fieldId={field.get('id')}
              hasMore={field.get('hasMore')}
              filters={field.get('fieldFilters')}
              handleExpandField={handleExpandField}
              handleToggleFilter={handleToggleFilter}
              activeFilters={activeFilters}
              initialIsExpanded={this.isFieldExpanded(field)}
              handleCloseExpandField={handleCloseExpandField}
              handleToggleExpandedField={handleToggleExpandedField}
            />
          </AboveTheFoldOnlyServerRender>
        );
        break;
      }
    }

    if (!fieldTag) {
      return null;
    }

    return (
      <li key={fieldId} className="field">
        {fieldTag}
      </li>
    );
  }

  render() {
    const { drawerActions, fieldsOrder } = this.props;
    return (
      <div className="cp-field-list">
        <ScreenReaderMessage>
          <FormattedMessage id="Facets.refineBy" tagName="h2" />
        </ScreenReaderMessage>
        <ul>{fieldsOrder.map(this.renderFields)}</ul>
        <div className="close-container">
          <Drawer.Toggle drawerActions={drawerActions}>
            <TextButton className="panel-close" handleClick={drawerActions.toggle} dataKey="field-list-button">
              <ChevronLeft className="panel-close-icon" />
              Close
            </TextButton>
          </Drawer.Toggle>
        </div>
      </div>
    );
  }
}

FieldList.propTypes = {
  activeFilters: activeFiltersShape.isRequired,
  fields: fieldsShape.isRequired,
  fieldsOrder: PropTypes.array.isRequired,
  handleExpandField: PropTypes.func,
  handleToggleFilter: PropTypes.func,
  handleToggleExpandedField: PropTypes.func,
  handleCloseExpandField: PropTypes.func,
  paginationField: PropTypes.string,
  renderSearchScope: PropTypes.func,
  transformQuery: PropTypes.func,
  singleBranch: PropTypes.bool,
  separator: PropTypes.string,
  prefix: PropTypes.string,
  drawerActions: PropTypes.objectOf(PropTypes.func).isRequired,
  router: routerShape.isRequired
};

FieldList.defaultProps = {
  paginationField: 'page',
  singleBranch: false
};

export default withRouter(FieldList);
